import React, { useState, useEffect, useRef } from "react";
import { useNavigate } from "react-router-dom";
import ContactAdminModal from "../../components/ContactAdminModal";
import ReportModal from "../../components/ReportModal";
import ProfileModal from "../../components/ProfileModal";
import AppointmentDetailsModal from "../../components/AppointmentDetailsModal";
import UserInfoModal from "../../components/UserInfoModal";
import AdditionalInfoModal from "../../components/AdditionalInfoModal";
import DashboardSidebar from "../../components/DashboardSidebar";
import { checkIsLoggedIn, handleFetchResponse } from "./helpers";
import { BACKEND_URL } from "../../utils/config.js";
import "./Dashboard.css";

const FRONTEND_URL = process.env.REACT_APP_FRONTEND_URL || "/backend";

// Helper function to get formatted service names from appointment
const getServiceNames = (appointment) => {
    // Handle new format: service_names array
    if (appointment.service_names && Array.isArray(appointment.service_names)) {
        return appointment.service_names.join(', ');
    }
    // Handle old format: single service_name string
    if (appointment.service_name) {
        return appointment.service_name;
    }
    // Fallback to service field
    if (appointment.service) {
        return appointment.service;
    }
    return 'N/A';
};

export default function ServiceWorkerDashboard() {
    const navigate = useNavigate();
    // Get user data from localStorage
    const [user] = useState({
        username: localStorage.getItem('username') || '',
        user_id: localStorage.getItem('user_id') || '',
        role: localStorage.getItem('role') || ''
    });
    const [upcomingAppointments, setUpcomingAppointments] = useState([]);
    const [pastAppointments, setPastAppointments] = useState([]);
    const [loading, setLoading] = useState(true);
    const [sidebarCollapsed, setSidebarCollapsed] = useState(false);
    const [mobileMenuOpen, setMobileMenuOpen] = useState(false);
    const [showDetailsMenu, setShowDetailsMenu] = useState(null);
    const [menuPosition, setMenuPosition] = useState({ top: 0, right: 0 });
    const menuHideTimeoutRef = useRef(null);
    const [calendarView, setCalendarView] = useState(false);
    const [currentMonth, setCurrentMonth] = useState(new Date());
    const [selectedAppointment, setSelectedAppointment] = useState(null);
    const [showAppointmentDetails, setShowAppointmentDetails] = useState(false);
    const [patientNames, setPatientNames] = useState({}); // Map of appointment_id to patient name
    const [availableServices, setAvailableServices] = useState([]);
    const [formData, setFormData] = useState({
        date: '',
        time: '',
        user_name: '',
        status: 'Pending',
        service: '',
        description: ''
    });
    const [error, setError] = useState(null);
    const [showContactModal, setShowContactModal] = useState(false);
    const [showReportModal, setShowReportModal] = useState(false);
    const [selectedReportId, setSelectedReportId] = useState(null);
    const [showAcceptDeclineModal, setShowAcceptDeclineModal] = useState(false);

    // Upcoming appointments filter states
    const [upcomingStatusFilter, setUpcomingStatusFilter] = useState('all');
    const [upcomingServiceFilter, setUpcomingServiceFilter] = useState('all');
    const [availableStatuses, setAvailableStatuses] = useState([]);
    const [pendingAppointmentForDecision, setPendingAppointmentForDecision] = useState(null);
    const [decisionLoading, setDecisionLoading] = useState(false);
    const [showSubmitReportModal, setShowSubmitReportModal] = useState(false);
    const [appointmentForReport, setAppointmentForReport] = useState(null);
    const [reportDescription, setReportDescription] = useState('');
    const [reportLoading, setReportLoading] = useState(false);
    
    // Profile modal state
    const [showProfileModal, setShowProfileModal] = useState(false);
    const [profileData, setProfileData] = useState({
        email: '',
        username: '',
        first_name: '',
        last_name: '',
        phone_number: '',
        date_of_birth: '',
        address: ''
    });
    const [profileFormData, setProfileFormData] = useState({
        email: '',
        username: '',
        first_name: '',
        last_name: '',
        phone_number: '',
        date_of_birth: '',
        address: ''
    });
    const [profileLoading, setProfileLoading] = useState(false);
    const [profileError, setProfileError] = useState(null);
    const [profileSuccess, setProfileSuccess] = useState(null);
    
    // Additional Info Modal state
    const [showAdditionalInfoModal, setShowAdditionalInfoModal] = useState(false);

    // User Info Modal state
    const [showUserInfo, setShowUserInfo] = useState(false);
    const [selectedUser, setSelectedUser] = useState(null);

    useEffect(() => {
        const fetchAppointments = async () => {
            try {
                let isLoggedin = await checkIsLoggedIn();
                if (!isLoggedin) {
                    navigate(`${FRONTEND_URL}/login`);
                    localStorage.clear();
                }

                const res = await fetch(`${BACKEND_URL}/appointments/psw`, {
                    method: "GET",
                    headers: {
                        "Content-Type": "application/json",
                    },
                    credentials: "include",
                });

                const checkedRes = await handleFetchResponse(res, navigate);
                if (!checkedRes) return;

                if (!checkedRes.ok) {
                    throw new Error(`Failed to fetch: ${checkedRes.status}`);
                }

                const data = await checkedRes.json();
                const now = new Date();

                const processedAppointments = data.appointments.map(a => ({
                    ...a,
                    id: a.appointment_id 
                }));

                const upcoming = processedAppointments.filter(a => new Date(a.start_date) >= now)
                    .sort((a, b) => new Date(a.start_date) - new Date(b.start_date));
                const past = processedAppointments.filter(a => new Date(a.start_date) < now)
                    .sort((a, b) => new Date(b.start_date) - new Date(a.start_date));

                // Extract unique services and statuses from upcoming appointments
                const uniqueServices = new Set();
                const uniqueStatuses = new Set();
                upcoming.forEach(apt => {
                    // Handle new format: service_names array
                    if (apt.service_names && Array.isArray(apt.service_names)) {
                        apt.service_names.forEach(service => uniqueServices.add(service));
                    }
                    // Handle old format: single service_name string
                    if (apt.service_name) uniqueServices.add(apt.service_name);
                    if (apt.service) uniqueServices.add(apt.service);
                    if (apt.status) uniqueStatuses.add(apt.status);
                });

                setAvailableServices(Array.from(uniqueServices).sort());
                setAvailableStatuses(Array.from(uniqueStatuses).sort());

                setUpcomingAppointments(upcoming);
                setPastAppointments(past);

            } catch (err) {
                setError(err.message);
            } finally {
                setLoading(false);
            }
        };

        const fetchServices = async () => {
            try {
                const res = await fetch(`${BACKEND_URL}/others/services`, {
                    credentials: 'include'
                });
                const checkedRes = await handleFetchResponse(res, navigate);
                if (checkedRes && checkedRes.ok) {
                    const services = await checkedRes.json();
                    // services should be an array of strings
                    setAvailableServices(Array.isArray(services) ? services : []);
                }
            } catch (err) {
                console.error('Error fetching services:', err);
                setAvailableServices([]);
            }
        };

        fetchAppointments();
        fetchServices();
    }, [BACKEND_URL]);

    const formatDateTime = (isoString) => {
        if (!isoString) return { date: 'N/A', time: 'N/A' };
        try {
            const dateObj = new Date(isoString);
            if (isNaN(dateObj.getTime())) {
                return { date: 'Invalid Date', time: 'Invalid Time' };
            }
            const date = dateObj.toLocaleDateString(undefined, { year: 'numeric', month: 'long', day: 'numeric' });
            const time = dateObj.toLocaleTimeString(undefined, { hour: '2-digit', minute: '2-digit', hour12: true });
            return { date, time };
        } catch (error) {
            console.error('Error formatting date:', error, isoString);
            return { date: 'N/A', time: 'N/A' };
        }
    };

    const getPatientName = (appointmentId) => {
        const appointment = upcomingAppointments.find(a => a.id === appointmentId) || 
                           pastAppointments.find(a => a.id === appointmentId);
        return appointment?.user_name || 'Unknown Patient';
    };

    const handleViewReports = () => {
        navigate(`${FRONTEND_URL}/report`);
    };

    const handleContactAdmin = () => {
        setShowContactModal(true);
    };

    const handleOpenProfileModal = async () => {
        try {
            // Load current user profile data from localStorage or fetch from backend
            const currentUsername = localStorage.getItem('username') || '';
            const currentUserId = localStorage.getItem('user_id') || '';
            
            // Try to fetch from backend if available
            let userData = {
                email: localStorage.getItem('email') || '',
                username: currentUsername,
                first_name: localStorage.getItem('first_name') || '',
                last_name: localStorage.getItem('last_name') || '',
                phone_number: localStorage.getItem('phone_number') || '',
                date_of_birth: localStorage.getItem('date_of_birth') || '',
                address: localStorage.getItem('address') || ''
            };

            // Attempt to fetch fresh data from backend
            try {
                const res = await fetch(`${BACKEND_URL}/users/current`, {
                    method: 'GET',
                    headers: {
                        'Content-Type': 'application/json',
                    },
                    credentials: 'include',
                });

                const checkedRes = await handleFetchResponse(res, navigate);
                if (checkedRes && checkedRes.ok) {
                    try {
                        const data = await checkedRes.json();
                        // Update userData with backend data, but preserve what we have if fields are missing
                        userData = {
                            email: data.email || userData.email,
                            username: data.username || userData.username,
                            first_name: data.first_name || userData.first_name,
                            last_name: data.last_name || userData.last_name,
                            phone_number: data.phone_number || userData.phone_number,
                            date_of_birth: data.date_of_birth || userData.date_of_birth,
                            address: data.address || userData.address
                        };
                    } catch (jsonError) {
                        console.error('Error parsing JSON:', jsonError);
                        console.log('Using localStorage data for profile');
                    }
                }
            } catch (err) {
                console.log('Using localStorage data for profile');
            }

            setProfileData(userData);
            setProfileFormData(userData);
            setShowProfileModal(true);
            setProfileError(null);
            setProfileSuccess(null);
        } catch (err) {
            setProfileError('Failed to load profile');
            console.error('Error loading profile:', err);
        }
    };

    const handleCloseProfileModal = () => {
        setShowProfileModal(false);
        setProfileFormData(profileData);
        setProfileError(null);
        setProfileSuccess(null);
    };

    const handleOpenAdditionalInfoModal = () => {
        setShowAdditionalInfoModal(true);
    };

    const handleCloseAdditionalInfoModal = () => {
        setShowAdditionalInfoModal(false);
    };

    const handleProfileInputChange = (e) => {
        const { name, value } = e.target;
        setProfileFormData(prev => ({
            ...prev,
            [name]: value
        }));
    };

    const handleSubmitProfile = async () => {
        setProfileLoading(true);
        setProfileError(null);
        setProfileSuccess(null);

        try {
            const response = await fetch(`${BACKEND_URL}/users/update`, {
                method: 'POST',
                headers: {
                    'Content-Type': 'application/json',
                },
                credentials: 'include',
                body: JSON.stringify(profileFormData)
            });

            const checkedResponse = await handleFetchResponse(response, navigate);
            if (!checkedResponse) return;

            if (!checkedResponse.ok) {
                const errorData = await checkedResponse.json();
                throw new Error(errorData.message || `Failed to update profile: ${checkedResponse.status}`);
            }

            // const result = await response.json();
            
            // Update localStorage with new values
            Object.keys(profileFormData).forEach(key => {
                localStorage.setItem(key, profileFormData[key]);
            });

            setProfileData(profileFormData);
            setProfileSuccess('Profile updated successfully!');
            
            // Close modal after success
            setTimeout(() => {
                handleCloseProfileModal();
            }, 1500);
        } catch (err) {
            setProfileError(err.message);
            console.error('Error updating profile:', err);
        } finally {
            setProfileLoading(false);
        }
    };

    const handleLogout = () => {
        // Clear user data from localStorage
        localStorage.removeItem('username');
        localStorage.removeItem('user_id');
        localStorage.removeItem('role');
        
        // Redirect to login page
        navigate(`${FRONTEND_URL}/login`);
    };

    const handleOpenUserInfo = async (patientId) => {
        try {
            const response = await fetch(`${BACKEND_URL}/admin/user/${patientId}`, {
                method: 'GET',
                headers: {
                    'Content-Type': 'application/json',
                },
                credentials: 'include'
            });

            const checkedResponse = await handleFetchResponse(response, navigate);
            if (!checkedResponse) return;

            if (!checkedResponse.ok) {
                throw new Error(`Failed to fetch patient details: ${checkedResponse.status}`);
            }

            const userData = await checkedResponse.json();
            setSelectedUser({
                ...userData,
                type: 'patient'
            });
            setShowUserInfo(true);
        } catch (err) {
            console.error('Error fetching patient details:', err);
            // Fallback: create basic user object from appointment
            const appointment = upcomingAppointments.find(a => a.id === patientId) || 
                               pastAppointments.find(a => a.id === patientId);
            if (appointment) {
                setSelectedUser({
                    id: patientId,
                    user_id: patientId,
                    patient_id: patientId,
                    name: appointment.user_name,
                    type: 'patient'
                });
                setShowUserInfo(true);
            }
        }
    };

    const handleCloseUserInfo = () => {
        setShowUserInfo(false);
        setSelectedUser(null);
    };

    const getUserSchedule = (userId, userType) => {
        if (userType === 'patient') {
            return upcomingAppointments.filter(apt => apt.patient_id === userId || apt.user_id === userId);
        }
        return [];
    };

    const handleCloseModals = () => {
        setShowDetailsMenu(null);
    };

    const handleViewDetails = (appointmentId, e) => {
        if (!appointmentId) {
            console.error('Appointment ID is missing');
            return;
        }
        
        if (e) {
            e.stopPropagation();
            e.preventDefault();
        }
        
        const isOpen = showDetailsMenu === appointmentId;
        setShowDetailsMenu(isOpen ? null : appointmentId);
        
        if (!isOpen && e && e.currentTarget) {
            setTimeout(() => {
                try {
                    const selector = `.details-menu-container[data-appointment-id="${appointmentId}"] .details-menu`;
                    const menuElement = document.querySelector(selector);
                    if (menuElement && e.currentTarget) {
                        const buttonRect = e.currentTarget.getBoundingClientRect();
                        const menuHeight = menuElement.offsetHeight || 150;
                        const viewportHeight = window.innerHeight;
                        const spaceBelow = viewportHeight - buttonRect.bottom;
                        const spaceAbove = buttonRect.top;
                        
                        // Set fixed position
                        menuElement.style.top = buttonRect.bottom + 8 + 'px';
                        menuElement.style.right = window.innerWidth - buttonRect.right + 'px';
                        
                        if (spaceBelow < menuHeight + 20 && spaceAbove > menuHeight + 20) {
                            menuElement.classList.add('menu-up');
                            menuElement.style.top = buttonRect.top - menuHeight - 8 + 'px';
                        } else {
                            menuElement.classList.remove('menu-up');
                        }
                    }
                } catch (error) {
                    console.error('Error positioning menu:', error);
                }
            }, 50);
        }
    };

    const updateMenuPosition = (appointmentId, containerElement) => {
        if (!containerElement) return;
        
        setTimeout(() => {
            try {
                // Find the button element within the container
                const buttonElement = containerElement.querySelector('.details-button');
                if (!buttonElement) return;
                
                const buttonRect = buttonElement.getBoundingClientRect();
                const menuHeight = 150; // Approximate menu height
                const viewportHeight = window.innerHeight;
                const spaceBelow = viewportHeight - buttonRect.bottom;
                const spaceAbove = buttonRect.top;
                
                let top = buttonRect.bottom + 8;
                const right = window.innerWidth - buttonRect.right;
                
                // If not enough space below but enough space above, open upward
                if (spaceBelow < menuHeight + 20 && spaceAbove > menuHeight + 20) {
                    top = buttonRect.top - menuHeight - 8;
                }
                
                setMenuPosition({ top, right });
            } catch (error) {
                console.error('Error positioning menu:', error);
            }
        }, 10);
    };

    const handleMouseEnter = (appointmentId, e) => {
        if (!appointmentId) {
            console.error('Appointment ID is missing');
            return;
        }
        
        // Clear any pending hide timeout
        if (menuHideTimeoutRef.current) {
            clearTimeout(menuHideTimeoutRef.current);
            menuHideTimeoutRef.current = null;
        }
        
        if (e) {
            e.stopPropagation();
        }
        
        setShowDetailsMenu(appointmentId);
        updateMenuPosition(appointmentId, e?.currentTarget);
    };

    const handleMouseLeave = () => {
        // Add a delay before hiding the menu (300ms) to give user time to move to menu
        menuHideTimeoutRef.current = setTimeout(() => {
            setShowDetailsMenu(null);
            menuHideTimeoutRef.current = null;
        }, 300);
    };

    const handleCalendarAppointmentClick = (appointment, e) => {
        if (e) e.stopPropagation();
        setSelectedAppointment(appointment);
        setShowAppointmentDetails(true);
    };

    const handleCloseAppointmentDetails = () => {
        setShowAppointmentDetails(false);
        setSelectedAppointment(null);
    };

    const handleCloseDetailsMenu = () => {
        setShowDetailsMenu(null);
    };

    const handleOpenAcceptDeclineModal = (appointment, e) => {
        if (e) {
            e.stopPropagation();
        }
        setPendingAppointmentForDecision(appointment);
        setShowAcceptDeclineModal(true);
        setShowDetailsMenu(null);
    };

    const handleCloseAcceptDeclineModal = () => {
        setShowAcceptDeclineModal(false);
        setPendingAppointmentForDecision(null);
    };

    const handleMakeDecision = async (decision) => {
        if (!pendingAppointmentForDecision || !pendingAppointmentForDecision.id) {
            console.error('No appointment selected for decision');
            return;
        }

        setDecisionLoading(true);
        try {
            const response = await fetch(`${BACKEND_URL}/appointments/${pendingAppointmentForDecision.id}`, {
                method: 'POST',
                headers: {
                    'Content-Type': 'application/json',
                },
                credentials: 'include',
                body: JSON.stringify({Status: { status: decision }})
            });

            const checkedResponse = await handleFetchResponse(response, navigate);
            if (!checkedResponse) return;

            if (!checkedResponse.ok) {
                throw new Error(`Failed to update appointment: ${checkedResponse.status}`);
            }

            // Update the appointment in state
            const updatedAppointment = {
                ...pendingAppointmentForDecision,
                status: decision
            };

            // Move appointment if needed
            const updatedUpcoming = upcomingAppointments.filter(apt => apt.id !== pendingAppointmentForDecision.id);
            const updatedPast = pastAppointments.filter(apt => apt.id !== pendingAppointmentForDecision.id);

            const now = new Date();
            if (new Date(pendingAppointmentForDecision.start_date) >= now) {
                setUpcomingAppointments([...updatedUpcoming, updatedAppointment].sort((a, b) => 
                    new Date(a.start_date) - new Date(b.start_date)
                ));
            } else {
                setPastAppointments([...updatedPast, updatedAppointment].sort((a, b) => 
                    new Date(b.start_date) - new Date(a.start_date)
                ));
            }

            handleCloseAcceptDeclineModal();
        } catch (err) {
            console.error('Error updating appointment:', err);
            alert(`Error: ${err.message}`);
        } finally {
            setDecisionLoading(false);
        }
    };

    const handleOpenSubmitReportModal = (appointment, e) => {
        if (e) {
            e.stopPropagation();
        }
        setAppointmentForReport(appointment);
        setReportDescription('');
        setShowSubmitReportModal(true);
        setShowDetailsMenu(null);
    };

    const handleCloseSubmitReportModal = () => {
        setShowSubmitReportModal(false);
        setAppointmentForReport(null);
        setReportDescription('');
    };

    const handleSubmitReport = async (app_status) => {
        if (!reportDescription.trim()) {
            alert('Please enter a report description');
            return;
        }

        if (!appointmentForReport || !appointmentForReport.report_id) {
            console.error('Report ID is missing');
            alert('Error: Report ID is missing');
            return;
        }

        setReportLoading(true);
        try {
            const res = await fetch(`${BACKEND_URL}/reports/${appointmentForReport.report_id}`, {
                method: 'POST',
                headers: {
                    'Content-Type': 'application/json',
                },
                credentials: 'include',
                body: JSON.stringify({ description: reportDescription, status: app_status  })
            });

            const checkedRes = await handleFetchResponse(res, navigate);
            if (!checkedRes) return;

            if (!checkedRes.ok) {
                throw new Error(`Failed to submit report: ${checkedRes.status}`);
            }

            alert('Report submitted successfully!');
            handleCloseSubmitReportModal();
        } catch (err) {
            console.error('Error submitting report:', err);
            alert(`Error: ${err.message}`);
        } finally {
            setReportLoading(false);
        }
    };

    useEffect(() => {
        const handleClickOutside = (event) => {
            if (showDetailsMenu && !event.target.closest('.details-menu-container')) {
                setShowDetailsMenu(null);
            }
        };
        document.addEventListener('click', handleClickOutside);
        return () => document.removeEventListener('click', handleClickOutside);
    }, [showDetailsMenu]);

    const getStatusBadgeClass = (status) => {
        const statusLower = status?.toLowerCase() || '';
        switch (statusLower) {
            case 'pending':
                return 'status-badge status-pending';
            case 'assigned':
                return 'status-badge status-assigned';
            case 'accepted':
                return 'status-badge status-accepted';
            case 'rejected':
                return 'status-badge status-rejected';
            case 'serviced':
                return 'status-badge status-serviced';
            case 'reviewed':
                return 'status-badge status-reviewed';
            case 'completed':
                return 'status-badge status-completed';
            case 'cancelled':
                return 'status-badge status-cancelled';
            default:
                return 'status-badge';
        }
    };

    const getDaysInMonth = (date) => {
        const year = date.getFullYear();
        const month = date.getMonth();
        const firstDay = new Date(year, month, 1);
        const lastDay = new Date(year, month + 1, 0);
        const daysInMonth = lastDay.getDate();
        const startingDayOfWeek = firstDay.getDay();
        
        const days = [];
        for (let i = 0; i < startingDayOfWeek; i++) {
            days.push(null);
        }
        for (let i = 1; i <= daysInMonth; i++) {
            days.push(new Date(year, month, i));
        }
        return days;
    };

    const getAppointmentsForDate = (date) => {
        if (!date) return [];
        const dateStr = date.toISOString().split('T')[0];
        return upcomingAppointments
            .filter(apt => {
                if (!apt || !apt.start_date) return false;
                try {
                    const aptDate = new Date(apt.start_date).toISOString().split('T')[0];
                    return aptDate === dateStr;
                } catch (e) {
                    return false;
                }
            })
            .sort((a, b) => {
                try {
                    const timeA = new Date(a.start_date).getTime();
                    const timeB = new Date(b.start_date).getTime();
                    return timeA - timeB;
                } catch (e) {
                    return 0;
                }
            });
    };

    const getStatusInfo = (status) => {
        const statusLower = status ? status.toLowerCase() : '';
        if (statusLower === 'serviced' || statusLower === 'completed' || statusLower === 'reviewed') {
            return { label: status, class: 'status-success', color: '#10b981' };
        } else if (statusLower === 'pending' || statusLower === 'assigned' || statusLower === 'accepted') {
            return { label: status, class: 'status-warning', color: '#f59e0b' };
        } else if (statusLower === 'rejected' || statusLower === 'cancelled') {
            return { label: status, class: 'status-danger', color: '#ef4444' };
        }
        return { label: status || 'Unknown', class: '', color: '#64748b' };
    };

    const navigateMonth = (direction) => {
        setCurrentMonth(prev => {
            const newDate = new Date(prev);
            newDate.setMonth(prev.getMonth() + direction);
            return newDate;
        });
    };

    const monthNames = ['January', 'February', 'March', 'April', 'May', 'June', 
                       'July', 'August', 'September', 'October', 'November', 'December'];
    const dayNames = ['Sun', 'Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat'];

    if (loading) {
        return (
            <div className="dashboard-page">
                <div className="loading-container">
                    <div className="loading-spinner"></div>
                    <p className="loading-message">Loading appointments...</p>
                </div>
            </div>
        );
    }

    return (
        <div className="dashboard-page">
            <DashboardSidebar
                sidebarCollapsed={sidebarCollapsed}
                onToggleSidebar={() => setSidebarCollapsed(!sidebarCollapsed)}
                mobileMenuOpen={mobileMenuOpen}
                onToggleMobileMenu={() => setMobileMenuOpen(!mobileMenuOpen)}
                profileTitle={`Service Worker${user.username ? ` - ${user.username}` : ''}`}
                profileSubtitle="Manage your appointments"
                buttons={[
                    {
                        icon: '👤',
                        text: 'Profile',
                        onClick: handleOpenProfileModal,
                        ariaLabel: 'View profile',
                        title: 'View Profile'
                    },
                    {
                        icon: '📄',
                        text: 'Additional Information',
                        onClick: () => {
                            handleOpenAdditionalInfoModal();
                            setMobileMenuOpen(false);
                        },
                        ariaLabel: 'Additional Information',
                        title: 'Additional Information'
                    },
                    {
                        icon: '📅',
                        text: 'Open Appointments',
                        onClick: () => {
                            navigate(`${FRONTEND_URL}/open-appointments`);
                            setMobileMenuOpen(false);
                        },
                        ariaLabel: 'View open appointments',
                        title: 'View Open Appointments'
                    },
                    {
                        icon: '📊',
                        text: 'View Reports',
                        onClick: () => {
                            handleViewReports();
                            setMobileMenuOpen(false);
                        },
                        ariaLabel: 'View reports',
                        title: 'View Reports'
                    },
                    {
                        icon: '✉️',
                        text: 'Contact Admin',
                        onClick: handleContactAdmin,
                        ariaLabel: 'Contact admin',
                        title: 'Contact Admin'
                    },
                    {
                        icon: '🚪',
                        text: 'Logout',
                        onClick: handleLogout,
                        className: 'logout-button',
                        ariaLabel: 'Logout',
                        title: 'Logout'
                    }
                ]}
            />

            <div className="dashboard-main">
                <section className="dashboard-card">
                    <div className="dashboard-title-row">
                        <h3>Upcoming Appointments</h3>
                        <div className="title-actions">
                            <button
                                className="view-toggle-button"
                                onClick={() => setCalendarView(!calendarView)}
                                aria-label={calendarView ? "Switch to list view" : "Switch to calendar view"}
                            >
                                {calendarView ? '📋 List' : '📅 Calendar'}
                            </button>
                            <small>{upcomingAppointments.length} items</small>
                        </div>
                    </div>

                    {/* Filter Section */}
                    <div style={{ marginBottom: '20px', padding: '16px', background: '#f8f9fa', borderRadius: '8px' }}>
                        <div style={{ display: 'flex', gap: '12px', alignItems: 'center', flexWrap: 'wrap' }}>
                            <label style={{ fontWeight: '600' }}>Filter by:</label>
                            <select
                                value={upcomingStatusFilter}
                                onChange={(e) => setUpcomingStatusFilter(e.target.value)}
                                style={{ padding: '8px 12px', borderRadius: '6px', border: '1px solid #e2e8f0' }}
                            >
                                <option value="all">All Statuses</option>
                                {availableStatuses.map(status => (
                                    <option key={status} value={status}>{status}</option>
                                ))}
                            </select>

                            <select
                                value={upcomingServiceFilter}
                                onChange={(e) => setUpcomingServiceFilter(e.target.value)}
                                style={{ padding: '8px 12px', borderRadius: '6px', border: '1px solid #e2e8f0' }}
                            >
                                <option value="all">All Services</option>
                                {availableServices.map(service => (
                                    <option key={service} value={service}>{service}</option>
                                ))}
                            </select>

                            {(upcomingStatusFilter !== 'all' || upcomingServiceFilter !== 'all') && (
                                <button
                                    onClick={() => {
                                        setUpcomingStatusFilter('all');
                                        setUpcomingServiceFilter('all');
                                    }}
                                    style={{
                                        padding: '8px 12px',
                                        background: '#e2e8f0',
                                        border: 'none',
                                        borderRadius: '6px',
                                        cursor: 'pointer',
                                        fontSize: '14px',
                                        fontWeight: '500'
                                    }}
                                >
                                    Clear Filters
                                </button>
                            )}
                        </div>
                    </div>
                    {calendarView ? (
                        <div className="calendar-container">
                            <div className="calendar-header">
                                <button 
                                    className="calendar-nav-button"
                                    onClick={() => navigateMonth(-1)}
                                    aria-label="Previous month"
                                >
                                    ‹
                                </button>
                                <h4 className="calendar-month-year">
                                    {monthNames[currentMonth.getMonth()]} {currentMonth.getFullYear()}
                                </h4>
                                <button 
                                    className="calendar-nav-button"
                                    onClick={() => navigateMonth(1)}
                                    aria-label="Next month"
                                >
                                    ›
                                </button>
                            </div>
                            <div className="calendar-legend">
                                <div className="legend-item">
                                    <div className="legend-color status-success"></div>
                                    <span>Completed</span>
                                </div>
                                <div className="legend-item">
                                    <div className="legend-color status-warning"></div>
                                    <span>Pending</span>
                                </div>
                                <div className="legend-item">
                                    <div className="legend-color status-danger"></div>
                                    <span>Cancelled</span>
                                </div>
                            </div>
                            <div className="calendar-grid">
                                {dayNames.map(day => (
                                    <div key={day} className="calendar-day-header">{day}</div>
                                ))}
                                {getDaysInMonth(currentMonth).map((date, index) => {
                                    const appointments = getAppointmentsForDate(date);
                                    const isToday = date && 
                                        date.toDateString() === new Date().toDateString();
                                    const isWeekend = date && (date.getDay() === 0 || date.getDay() === 6);
                                    
                                    return (
                                        <div 
                                            key={index} 
                                            className={`calendar-day ${!date ? 'empty' : ''} ${isToday ? 'today' : ''} ${isWeekend ? 'weekend' : ''}`}
                                        >
                                            {date && (
                                                <>
                                                    <div className="calendar-day-number">{date.getDate()}</div>
                                                    <div className="calendar-day-appointments">
                                                        {appointments.slice(0, 3).map(apt => {
                                                            if (!apt) return null;
                                                            const { time } = formatDateTime(apt.start_date);
                                                            const statusClass = getStatusBadgeClass(apt.status).split(' ')[1] || '';
                                                            const patientName = getPatientName(apt.id);
                                                            return (
                                                                <div 
                                                                    key={apt.id}
                                                                    className={`calendar-appointment ${statusClass}`}
                                                                    onClick={(e) => handleCalendarAppointmentClick(apt, e)}
                                                                    title={`${time} - ${patientName}`}
                                                                >
                                                                    <span className="appointment-time">{time}</span>
                                                                    <span className="appointment-psw">{patientName}</span>
                                                                </div>
                                                            );
                                                        })}
                                                        {appointments.length > 3 && (
                                                            <div className="calendar-more-appointments">
                                                                +{appointments.length - 3} more
                                                            </div>
                                                        )}
                                                    </div>
                                                </>
                                            )}
                                        </div>
                                    );
                                })}
                            </div>
                        </div>
                    ) : (
                        <div className="table-container">
                            <table className="dashboard-table" role="table" aria-label="Upcoming appointments">
                            <thead>
                                <tr>
                                    <th>Date</th>
                                    <th>Time</th>
                                    <th>Patient Name</th>
                                    <th>Service</th>
                                    <th>Status</th>
                                    <th>Actions</th>
                                </tr>
                            </thead>
                            <tbody>
                                {upcomingAppointments.filter(a => {
                                    let matchesStatus = upcomingStatusFilter === 'all' || a.status === upcomingStatusFilter;
                                    let matchesService = upcomingServiceFilter === 'all' || getServiceNames(a) === upcomingServiceFilter || 
                                        (a.service_names && Array.isArray(a.service_names) && a.service_names.includes(upcomingServiceFilter));
                                    return matchesStatus && matchesService;
                                }).map((a, index) => {
                                    const { date, time } = formatDateTime(a.start_date);
                                    const patientName = getPatientName(a.id);
                                    
                                    return (
                                        <tr key={a.id} className={index % 2 === 0 ? 'even-row' : 'odd-row'}>
                                            <td>{date}</td>
                                            <td>{time}</td>
                                            <td className="caregiver-id">{patientName}</td>
                                            <td>{getServiceNames(a)}</td>
                                            <td>
                                                <span className={getStatusBadgeClass(a.status)}>
                                                    {a.status}
                                                </span>
                                            </td>
                                            <td>
                                                <div className="actions-cell">
                                                    <div 
                                                        className="details-menu-container"
                                                        data-appointment-id={a.id}
                                                        onMouseEnter={(e) => handleMouseEnter(a.id, e)}
                                                        onMouseLeave={handleMouseLeave}
                                                    >
                                                        <button
                                                            className="details-button"
                                                            aria-label="View details"
                                                        >
                                                            <svg width="16" height="16" viewBox="0 0 16 16" fill="none" xmlns="http://www.w3.org/2000/svg">
                                                                <circle cx="8" cy="3" r="1.5" fill="currentColor"/>
                                                                <circle cx="8" cy="8" r="1.5" fill="currentColor"/>
                                                                <circle cx="8" cy="13" r="1.5" fill="currentColor"/>
                                                            </svg>
                                                        </button>
                                                        {showDetailsMenu === a.id && (
                                                            <div 
                                                                className="details-menu"
                                                                style={{ top: `${menuPosition.top}px`, right: `${menuPosition.right}px` }}
                                                                onMouseEnter={() => {
                                                                    // Clear any pending hide timeout when hovering over menu
                                                                    if (menuHideTimeoutRef.current) {
                                                                        clearTimeout(menuHideTimeoutRef.current);
                                                                        menuHideTimeoutRef.current = null;
                                                                    }
                                                                    setShowDetailsMenu(a.id);
                                                                }}
                                                                onMouseLeave={handleMouseLeave}
                                                            >
                                                                {a.status && a.status.toLowerCase() === 'assigned' && (
                                                                    <button
                                                                        className="details-menu-item"
                                                                        onClick={() => handleOpenAcceptDeclineModal(a)}
                                                                    >
                                                                        <span className="menu-icon">⚖️</span>
                                                                        Accept/Decline
                                                                    </button>
                                                                )}
                                                                {a.status && a.status.toLowerCase() === 'serviced' && (
                                                                    <button
                                                                        className="details-menu-item"
                                                                        onClick={() => handleOpenSubmitReportModal(a)}
                                                                    >
                                                                        <span className="menu-icon">📝</span>
                                                                        Submit Report
                                                                    </button>
                                                                )}
                                                                <button
                                                                    className="details-menu-item"
                                                                    onClick={() => {
                                                                        setSelectedAppointment(a);
                                                                        setShowAppointmentDetails(true);
                                                                        setShowDetailsMenu(null);
                                                                    }}
                                                                >
                                                                    <span className="menu-icon">👁️</span>
                                                                    View Full Details
                                                                </button>
                                                                <button
                                                                    className="details-menu-item"
                                                                    onClick={() => {
                                                                        handleOpenUserInfo(a.patient_id || a.user_id);
                                                                        setShowDetailsMenu(null);
                                                                    }}
                                                                >
                                                                    <span className="menu-icon">👤</span>
                                                                    View Patient Info
                                                                </button>
                                                            </div>
                                                        )}
                                                    </div>
                                                </div>
                                            </td>
                                        </tr>
                                    );
                                })}
                                {upcomingAppointments.length === 0 && (
                                    <tr>
                                        <td colSpan="6" className="empty-state">
                                            <div className="empty-state-content">
                                                <span className="empty-icon">📅</span>
                                                <p>No upcoming appointments</p>
                                            </div>
                                        </td>
                                    </tr>
                                )}
                            </tbody>
                        </table>
                    </div>
                    )}
                </section>

                <section className="dashboard-card">
                    <div className="dashboard-title-row">
                        <h3>Past Appointments</h3>
                        <small>{pastAppointments.length} items</small>
                    </div>
                    <div className="table-container">
                        <table className="dashboard-table" role="table" aria-label="Past appointments">
                            <thead>
                                <tr>
                                    <th>Date</th>
                                    <th>Time</th>
                                    <th>Patient Name</th>
                                    <th>Service</th>
                                    <th>Status</th>
                                    <th>Actions</th>
                                </tr>
                            </thead>
                            <tbody>
                                {pastAppointments.map((a, index) => {
                                    if (!a || !a.id) return null;
                                    const { date, time } = formatDateTime(a.start_date);
                                    const patientName = getPatientName(a.id);
                                    return (
                                        <tr key={a.id} className={index % 2 === 0 ? 'even-row' : 'odd-row'}>
                                            <td>{date}</td>
                                            <td>{time}</td>
                                            <td className="caregiver-id">{patientName}</td>
                                            <td>{getServiceNames(a)}</td>
                                            <td>
                                                <span className={getStatusBadgeClass(a.status)}>
                                                    {a.status || 'N/A'}
                                                </span>
                                            </td>
                                            <td>
                                                <div className="actions-cell">
                                                    <div 
                                                        className="details-menu-container" 
                                                        data-appointment-id={a.id}
                                                        onMouseEnter={(e) => handleMouseEnter(a.id, e)}
                                                        onMouseLeave={handleMouseLeave}
                                                    >
                                                        <button
                                                            className="details-button"
                                                            aria-label="View details"
                                                        >
                                                            <svg width="16" height="16" viewBox="0 0 16 16" fill="none" xmlns="http://www.w3.org/2000/svg">
                                                                <circle cx="8" cy="3" r="1.5" fill="currentColor"/>
                                                                <circle cx="8" cy="8" r="1.5" fill="currentColor"/>
                                                                <circle cx="8" cy="13" r="1.5" fill="currentColor"/>
                                                            </svg>
                                                        </button>
                                                        {showDetailsMenu === a.id && (
                                                            <div className="details-menu" style={{ top: `${menuPosition.top}px`, right: `${menuPosition.right}px` }} onMouseEnter={() => clearTimeout(menuHideTimeoutRef.current)} onMouseLeave={handleMouseLeave}>
                                                                <button
                                                                    className="details-menu-item"
                                                                    onClick={() => {
                                                                        setSelectedAppointment(a);
                                                                        setShowAppointmentDetails(true);
                                                                        setShowDetailsMenu(null);
                                                                    }}
                                                                >
                                                                    <span className="menu-icon">👁️</span>
                                                                    View Full Details
                                                                </button>
                                                                <button
                                                                    className="details-menu-item"
                                                                    onClick={() => {
                                                                        handleOpenUserInfo(a.patient_id || a.user_id);
                                                                        setShowDetailsMenu(null);
                                                                    }}
                                                                >
                                                                    <span className="menu-icon">👤</span>
                                                                    View Patient Info
                                                                </button>
                                                                <button
                                                                    className="details-menu-item"
                                                                    onClick={() => {
                                                                        setSelectedReportId(a.report_id || a.id);
                                                                        setShowReportModal(true);
                                                                        setShowDetailsMenu(null);
                                                                    }}
                                                                >
                                                                    <span className="menu-icon">📄</span>
                                                                    View Report
                                                                </button>
                                                            </div>
                                                        )}
                                                    </div>
                                                </div>
                                            </td>
                                        </tr>
                                    );
                                })}
                                {pastAppointments.length === 0 && (
                                    <tr>
                                        <td colSpan="6" className="empty-state">
                                            <div className="empty-state-content">
                                                <span className="empty-icon">📋</span>
                                                <p>No past appointments</p>
                                            </div>
                                        </td>
                                    </tr>
                                )}
                            </tbody>
                        </table>
                    </div>
                </section>
            </div>

            {/* Appointment Details Modal */}
            <AppointmentDetailsModal
                isOpen={showAppointmentDetails}
                appointment={selectedAppointment}
                onClose={handleCloseAppointmentDetails}
                formatDateTime={formatDateTime}
                getPatientName={getPatientName}
                getStatusBadgeClass={getStatusBadgeClass}
                onEditAppointment={null}
                isUpcomingAppointment={selectedAppointment && new Date(selectedAppointment.start_date) >= new Date()}
            />

            <UserInfoModal
                isOpen={showUserInfo}
                user={selectedUser}
                onClose={handleCloseUserInfo}
                formatDateTime={formatDateTime}
                getStatusBadgeClass={getStatusBadgeClass}
                getUserSchedule={getUserSchedule}
            />

            <ContactAdminModal 
                isOpen={showContactModal} 
                onClose={() => setShowContactModal(false)} 
            />

            <ReportModal 
                isOpen={showReportModal}
                onClose={() => {
                    setShowReportModal(false);
                    setSelectedReportId(null);
                }}
                reportId={selectedReportId}
            />

            {/* Profile Modal */}
            <ProfileModal
                showModal={showProfileModal}
                onClose={handleCloseProfileModal}
                profileFormData={profileFormData}
                onInputChange={handleProfileInputChange}
                onSubmit={handleSubmitProfile}
                loading={profileLoading}
                error={profileError}
                success={profileSuccess}
            />

            {/* Accept/Decline Decision Modal */}
            {showAcceptDeclineModal && pendingAppointmentForDecision && (
                <div className="modal-overlay" onClick={handleCloseAcceptDeclineModal}>
                    <div className="modal-content appointment-details-modal" onClick={(e) => e.stopPropagation()}>
                        <div className="modal-header">
                            <h2>Review Appointment</h2>
                            <button className="modal-close" onClick={handleCloseAcceptDeclineModal} aria-label="Close modal">
                                ×
                            </button>
                        </div>
                        <div className="appointment-details-content">
                            <div className="detail-row">
                                <span className="detail-label">Date:</span>
                                <span className="detail-value">{formatDateTime(pendingAppointmentForDecision.start_date).date}</span>
                            </div>
                            <div className="detail-row">
                                <span className="detail-label">Time:</span>
                                <span className="detail-value">{formatDateTime(pendingAppointmentForDecision.start_date).time}</span>
                            </div>
                            <div className="detail-row">
                                <span className="detail-label">Patient Name:</span>
                                <span className="detail-value caregiver-id">{getPatientName(pendingAppointmentForDecision.id)}</span>
                            </div>
                            <div className="detail-row">
                                <span className="detail-label">Service:</span>
                                <span className="detail-value">{getServiceNames(pendingAppointmentForDecision)}</span>
                            </div>
                            <div className="detail-row">
                                <span className="detail-label">Status:</span>
                                <span className={`status-badge ${getStatusBadgeClass(pendingAppointmentForDecision.status)}`}>
                                    {pendingAppointmentForDecision.status || 'N/A'}
                                </span>
                            </div>
                            {pendingAppointmentForDecision.description && (
                                <div className="detail-row">
                                    <span className="detail-label">Description:</span>
                                    <span className="detail-value">{pendingAppointmentForDecision.description}</span>
                                </div>
                            )}
                        </div>
                        <div className="modal-actions">
                            <button 
                                type="button" 
                                className="modal-button-cancel" 
                                onClick={handleCloseAcceptDeclineModal}
                                disabled={decisionLoading}
                            >
                                Cancel
                            </button>
                            <button 
                                type="button" 
                                className="modal-button-cancel" 
                                onClick={() => handleMakeDecision('rejected')}
                                disabled={decisionLoading}
                            >
                                {decisionLoading ? 'Processing...' : 'Decline'}
                            </button>
                            <button 
                                type="button" 
                                className="modal-button-submit"
                                onClick={() => handleMakeDecision('accepted')}
                                disabled={decisionLoading}
                            >
                                {decisionLoading ? 'Processing...' : 'Accept'}
                            </button>
                        </div>
                    </div>
                </div>
            )}

            {/* Submit Report Modal */}
            {showSubmitReportModal && appointmentForReport && (
                <div className="modal-overlay" onClick={handleCloseSubmitReportModal}>
                    <div className="modal-content appointment-details-modal" onClick={(e) => e.stopPropagation()}>
                        <div className="modal-header">
                            <h2>Submit Report</h2>
                            <button className="modal-close" onClick={handleCloseSubmitReportModal} aria-label="Close modal">
                                ×
                            </button>
                        </div>
                        <div className="appointment-details-content">
                            <div className="detail-row">
                                <span className="detail-label">Appointment Date:</span>
                                <span className="detail-value">{formatDateTime(appointmentForReport.start_date).date}</span>
                            </div>
                            <div className="detail-row">
                                <span className="detail-label">Patient Name:</span>
                                <span className="detail-value caregiver-id">{getPatientName(appointmentForReport.id)}</span>
                            </div>
                            <div className="detail-row">
                                <span className="detail-label">Service:</span>
                                <span className="detail-value">{getServiceNames(appointmentForReport)}</span>
                            </div>
                            <div style={{ marginTop: '24px', marginBottom: '16px', borderTop: '1px solid #e2e8f0', paddingTop: '16px' }}>
                                <label style={{ display: 'block', marginBottom: '8px', fontWeight: '600', color: '#1e293b' }}>
                                    Report Description:
                                </label>
                                <textarea
                                    value={reportDescription}
                                    onChange={(e) => setReportDescription(e.target.value)}
                                    placeholder="Enter your report description here..."
                                    style={{
                                        width: '100%',
                                        minHeight: '120px',
                                        padding: '10px 12px',
                                        borderRadius: '6px',
                                        border: '1px solid #e2e8f0',
                                        fontSize: '14px',
                                        fontFamily: 'inherit',
                                        backgroundColor: '#ffffff',
                                        resize: 'vertical'
                                    }}
                                />
                            </div>
                        </div>
                        <div className="modal-actions">
                            <button
                                type="button"
                                className="modal-button-cancel"
                                onClick={handleCloseSubmitReportModal}
                                disabled={reportLoading}
                            >
                                Cancel
                            </button>
                            <button
                                type="button"
                                className="modal-button-submit"
                                onClick={() => handleSubmitReport(appointmentForReport.status)}
                                disabled={reportLoading || !reportDescription.trim()}
                                style={{
                                    opacity: (reportLoading || !reportDescription.trim()) ? 0.6 : 1,
                                    cursor: (reportLoading || !reportDescription.trim()) ? 'not-allowed' : 'pointer'
                                }}
                            >
                                {reportLoading ? 'Submitting...' : 'Submit Report'}
                            </button>
                        </div>
                    </div>
                </div>
            )}

            {/* Additional Info Modal */}
            <AdditionalInfoModal
                isOpen={showAdditionalInfoModal}
                onClose={handleCloseAdditionalInfoModal}
                pswId={user.user_id}
            />
        </div>
    );
}

